import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check } from "lucide-react";
import { SUBCATS, START_MONTHS } from "@/lib/calc";

const Section = ({ n, title, children, cls = "" }) => (
  <section className={`fade-up rounded-lg border border-plum-900/10 bg-white p-5 ${cls}`}>
    <div className="mb-4 flex items-center gap-3">
      <span className="font-num text-xs text-plum-700">{n}</span>
      <h2 className="font-display text-base font-bold text-plum-950">{title}</h2>
    </div>
    {children}
  </section>
);

const Toggle = ({ on, onClick, label, testid, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    data-checked={on}
    className={`flex w-full items-center gap-2.5 rounded-md border px-3 py-2.5 text-left text-sm font-medium transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-40 ${
      on ? "border-plum-900 bg-plum-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
    }`}
  >
    <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-[3px] border ${on ? "border-plum-300 bg-plum-400 text-plum-950" : "border-slate-300"}`}>
      {on && <Check className="h-3 w-3" strokeWidth={3.5} />}
    </span>
    {label}
  </button>
);

const TextField = ({ label, id, value, onChange, type = "text" }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-semibold text-slate-700">{label}</Label>
    <Input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={`input-${id}`} className="bg-white" />
  </div>
);

export const ReceiptForm = ({ v, set, grades, spotLocked }) => {
  const upd = (k) => (val) => set({ ...v, [k]: val });
  const pickSub = (k) => set({ ...v, sub: Object.fromEntries(SUBCATS.map((s) => [s.key, s.key === k])) });
  const pickCat = (spot) => set({ ...v, spot, revisit: !spot });
  return (
    <div className="space-y-4">
      <Section n="01" title="Student & Parent">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="Date of Enquiry" id="enquiry-date" type="date" value={v.enquiryDate} onChange={upd("enquiryDate")} />
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">Admission Seeking Grade</Label>
            <Select value={v.grade} onValueChange={upd("grade")}>
              <SelectTrigger data-testid="select-grade" className="bg-white"><SelectValue placeholder="Select grade" /></SelectTrigger>
              <SelectContent>
                {grades.map((g) => <SelectItem key={g} value={g} data-testid={`grade-option-${g.replace(/\s+/g, "-").toLowerCase()}`}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <TextField label="Child Name" id="child-name" value={v.childName} onChange={upd("childName")} />
          <TextField label="Parent Name" id="parent-name" value={v.parentName} onChange={upd("parentName")} />
          <TextField label="Contact (WhatsApp)" id="contact" value={v.contact} onChange={upd("contact")} />
          <TextField label="Parent Email" id="email" type="email" value={v.email || ""} onChange={upd("email")} />
          <div className="space-y-1.5">
            <Label htmlFor="counselor-name" className="text-xs font-semibold text-slate-700">Adm Counsellor Name</Label>
            <Input id="counselor-name" value={v.counselorName} readOnly tabIndex={-1} data-testid="input-counselor-name" className="cursor-not-allowed bg-plum-50 font-medium text-plum-900" />
            <p className="text-[11px] text-slate-500">Filled from your staff login</p>
          </div>
        </div>
      </Section>

      <Section n="02" title="Category" cls="d1">
        <div className="grid grid-cols-6 gap-2">
          <div className="col-span-6 text-xs font-semibold text-slate-700">Category</div>
          <div className="col-span-3"><Toggle on={v.spot} onClick={() => pickCat(true)} label="Spot" testid="toggle-spot" disabled={spotLocked} /></div>
          <div className="col-span-3"><Toggle on={v.revisit} onClick={() => pickCat(false)} label="Revisit" testid="toggle-revisit" /></div>
          <div className="col-span-6 mt-3 text-xs font-semibold text-slate-700">Sub Category</div>
          {SUBCATS.map((s, i) => (
            <div key={s.key} className={i < 3 ? "col-span-6 sm:col-span-2" : "col-span-6 sm:col-span-3"}>
              <Toggle on={v.sub[s.key]} onClick={() => pickSub(s.key)} label={s.label} testid={`toggle-${s.key}`} />
            </div>
          ))}
          {v.sub.referral && (
            <div className="col-span-6 mt-2 grid gap-3 rounded-md border border-plum-200 bg-plum-50/60 p-3 sm:grid-cols-2" data-testid="referral-fields">
              <TextField label="Referral Name *" id="referral-name" value={v.referralName || ""} onChange={upd("referralName")} />
              <TextField label="Referral Mobile No. *" id="referral-mobile" type="tel" value={v.referralMobile || ""} onChange={upd("referralMobile")} />
            </div>
          )}
          {v.sub.existingSibling && (
            <div className="col-span-6 mt-2 grid gap-3 rounded-md border border-plum-200 bg-plum-50/60 p-3 sm:grid-cols-2" data-testid="sibling-fields">
              <TextField label="Sibling Full Name *" id="sibling-name" value={v.siblingName || ""} onChange={upd("siblingName")} />
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Sibling Grade *</Label>
                <Select value={v.siblingGrade || ""} onValueChange={upd("siblingGrade")}>
                  <SelectTrigger data-testid="select-sibling-grade" className="bg-white"><SelectValue placeholder="Select sibling's grade" /></SelectTrigger>
                  <SelectContent>
                    {grades.map((g) => <SelectItem key={g} value={g} data-testid={`sibling-grade-option-${g.replace(/\s+/g, "-").toLowerCase()}`}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
        <p className="mt-3 text-xs text-slate-500">{spotLocked ? <span className="font-semibold text-red-700" data-testid="spot-locked-hint">Spot is not applicable — this parent has visited earlier.</span> : "Select one category and one sub-category. Spot needs the Rs.1000 form fee on the enquiry date and the admission fee within 5 days of the enquiry."}</p>
      </Section>

      <Section n="03" title="Payment & Remarks" cls="d2">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">Monthly Option · Start Month (ends January)</Label>
            <Select value={v.startMonth} onValueChange={upd("startMonth")}>
              <SelectTrigger data-testid="select-start-month" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {START_MONTHS.map((m) => <SelectItem key={m} value={m} data-testid={`month-option-${m.toLowerCase()}`}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="remarks" className="text-xs font-semibold text-slate-700">Remarks (if any)</Label>
            <Textarea id="remarks" rows={3} value={v.remarks} onChange={(e) => upd("remarks")(e.target.value)} data-testid="input-remarks" className="bg-white" />
          </div>
        </div>
      </Section>
    </div>
  );
};
